import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbs } from '@/features/header';

import { useNavigate, useParams } from 'react-router-dom';
import { OpportunityCollapsableProjects, OpportunityHeaderView, OpportunityStepper } from '../../opportunity-view';
import { useOpportunityDetails } from '@/features/opportunities/hooks';
import { OpportunityDealManager } from '../../opportunity-view/opportunity-details/opportunity-deal-manager';
import { AcademyInfo } from '../academy';
import { OpportunityDetails } from '../../opportunity-view/opportunity-details/opportunity-details';
import { useBelongsToMyCompany } from '@/features/company/hooks';
import { useCurrentUser } from '@/features/users/hooks';
import { projectItemListTransformer } from '@/features/projects/helpers';
import { serviceItemListTransformer } from '@/features/services/helpers';
import { OpportunitySellerPricingCards } from '../../opportunity-view/opportunity-details/opportunity-seller-pricing-cards';
import { OpportunityServices } from '../../opportunity-view/opportunity-details/opportunity-services';
import { OpportunityBiddersCard } from '../../opportunity-view/opportunity-offers-view';
import { OpportunitySellerStats } from '../../opportunity-view/opportunity-details/opportunity-seller-stats';
import { Container, Link, Typography } from '@/components';
import { useSidebar } from '@/components/ui/sidebar';
import { Route } from '@/constants';
import { cn } from '@/utils/styles';

function OpportunitySellerInfo({ projects, names, opportunityId, isPaid, belongsToMe }: any) {
  return (
    <>
      <OpportunityDetails opportunityId={opportunityId} />
      <OpportunityCollapsableProjects
        projects={projects}
        names={names}
        opportunityId={opportunityId}
        isPaid={isPaid}
        belongsToMe={belongsToMe}
      />
    </>
  );
}

export function MyOpportunityDetail() {
  const { t } = useTranslation(['opportunities']);
  const { opportunityId: id } = useParams();
  const navigate = useNavigate();
  const { state } = useSidebar();

  if (!id) return null;

  const [isOpen, setIsOpen] = useState(false);
  const currentUser = useCurrentUser();
  useBreadcrumbs(() => [{ title: t(`Project`) }]);
  const isCollapsed = state === 'collapsed';

  const { normalizedOpportunity, opportunity } = useOpportunityDetails(id);
  const opportunityBelongsToMyCompany = useBelongsToMyCompany(normalizedOpportunity?.company);

  useEffect(() => {
    if (!normalizedOpportunity) return;
  }, [normalizedOpportunity?.id]);

  useEffect(() => {
    if (!normalizedOpportunity || opportunityBelongsToMyCompany === undefined) {
      return;
    }

    if (opportunityBelongsToMyCompany === false) {
      navigate(Route.Home);
    }
  }, [opportunityBelongsToMyCompany, normalizedOpportunity, navigate]);

  if (opportunityBelongsToMyCompany === undefined) {
    return <div>Loading...</div>;
  }

  const ndaFile = opportunity?.files.find(({ type }) => type === 'nda');

  const projects = opportunity?.projects.map((project) => project.project);
  const projectsName = opportunity?.projects.map((project) => project.name);

  const isOpportunityMine = normalizedOpportunity?.company === currentUser?.company?.id;
  const userHasOffered = opportunity?.offers?.some((offer) => offer.company?.id === currentUser?.company?.id);
  const canViewAllProjects = isOpportunityMine || userHasOffered;
  const filteredProjects = canViewAllProjects ? projects : projects?.filter((project) => project?.status !== 'closed');
  const projectItemList = filteredProjects?.map(projectItemListTransformer);
  const formattedServices = normalizedOpportunity?.advisors.map((advisor) => serviceItemListTransformer(advisor));
  const opportunityStatus = normalizedOpportunity?.status?.name;

  if (!normalizedOpportunity || !projectItemList || !projects || !opportunityBelongsToMyCompany) return null;

  return (
    <div className="mb-10 flex flex-col pb-20 lg:mb-5 lg:pb-10">
      <div
        className={cn(
          'bg-neutral-10 fixed right-0 left-0 border-b border-neutral-50 py-2 lg:py-4',
          'md:right-3',
          isCollapsed ? 'md:left-[60px]' : 'md:left-[220px]',
          'transition-all duration-150 ease-in-out'
        )}
        style={{ zIndex: 7 }}
      >
        <Container size="xl" className="space-y-6 px-4 sm:px-6 md:px-12">
          <OpportunityHeaderView id={id} setIsOpen={setIsOpen} />
        </Container>
      </div>

      <Container size="xl" className="w-full px-4 pt-16 sm:px-6 md:px-8 md:pt-24">
        {/* Stepper + Deal Manager */}
        <div className="mt-14 mb-6 grid grid-cols-1 gap-6 md:mt-4 lg:mt-0 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {normalizedOpportunity && (
              <OpportunityStepper
                status={normalizedOpportunity.status?.name}
                closedType={normalizedOpportunity.closedType}
              />
            )}
          </div>
          <div className="lg:col-span-1">
            <OpportunityDealManager />
          </div>
        </div>

        {/* Pricing Cards (solo pending) */}
        {opportunityStatus === 'pending' && (
          <div className="mb-8">
            <OpportunitySellerPricingCards opportunity={normalizedOpportunity} />
          </div>
        )}

        {/* Academy Info */}
        <div className="mb-8">
          <AcademyInfo
            status={opportunityStatus}
            opportunityId={id}
            initialVdrLink={normalizedOpportunity?.vdrLink ?? null}
            ndaFile={ndaFile}
          />
        </div>

        {/* Services (solo pending) */}
        {opportunityStatus === 'pending' && formattedServices && (
          <div className="mb-8">
            <OpportunityServices services={formattedServices} />
          </div>
        )}

        {/* Seller Info (no published/closed) */}
        {opportunityStatus !== 'published' && opportunityStatus !== 'closed' && opportunity && (
          <div className="space-y-4">
            <OpportunitySellerInfo
              projects={projectItemList}
              names={projectsName}
              opportunityId={id}
              isPaid={normalizedOpportunity?.isPaid}
              belongsToMe={opportunityBelongsToMyCompany}
              formattedServices={formattedServices}
            />
          </div>
        )}

        {/* Bidders + Stats (published/closed) */}
        {(opportunityStatus === 'published' || opportunityStatus === 'closed') && normalizedOpportunity && (
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <OpportunityBiddersCard opportunity={normalizedOpportunity} />
            </div>
            <div className="lg:col-span-1">
              <OpportunitySellerStats opportunity={normalizedOpportunity} />
            </div>
          </div>
        )}

        {/* Footer */}
        <Typography className="mt-6 italic" size="body-3">
          {t('If you have any questions, feel free to contact us at ')}
          <Link to="mailto:projects@nteaser.com">projects@nteaser.com</Link>.
        </Typography>
      </Container>
    </div>
  );
}
